/**
 * Asset panel tab "Betreiberpflichten": all obligations for the asset's
 * regulated object (resolved from its DIN 276 category via the crosswalk),
 * presented like the Betreiberverantwortung review queue (Pflicht,
 * Beschreibung, Tier, Typ, Herkunft — plus the review status, since
 * unconfirmed legal content must never look authoritative).
 *
 * On top: whether the asset is covered by an active subscription (binding);
 * if not, administrators can activate one for the asset's site × regulated
 * object — with the mandatory effect preview, like the settings panel.
 */

import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import type { Column, ColumnDef } from "@tanstack/react-table";

import { useModalContext } from "../components/ModalProvider";
import { authenticationAtom } from "../State";
import { useRoleAccess } from "../hooks/useRoleAccess";
import { useLegalBindingAPI } from "../LegalBindingAPI";
import {
  ReviewStatusBadge,
  ProvenanceBadge,
  WrappedText,
  regulatedObjectName,
} from "../administration/operator-responsibility/legalDisplay";
import PflichtklasseLegend from "../administration/operator-responsibility/PflichtklasseLegend";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Properties {
  asset: any;
}

/** Klickbarer Spaltenkopf — ersetzt die `sortable`-Köpfe der abgelösten Tabelle. */
function SortableHeader({
  column,
  children,
}: {
  column: Column<any, unknown>;
  children: ReactNode;
}) {
  const sorted = column.getIsSorted();

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      {children}
      {sorted === "asc" ? (
        <ArrowUp aria-hidden className="h-3.5 w-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDown aria-hidden className="h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
      )}
    </Button>
  );
}

export default function AssetObligationsPanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const [authentication] = useAtom(authenticationAtom) as any;
  const { isCustomerAdmin, hasFullAccess } = useRoleAccess();
  const legalAPI = useLegalBindingAPI(authentication?.customer);
  const conformanceClassId = useId();

  // A manufacturer viewing a foreign asset reads the owner's obligations, never writes them.
  const canWrite = (isCustomerAdmin || hasFullAccess) && !properties.asset?._manufacturerView;
  const assetId = properties.asset?._id ? String(properties.asset._id) : null;

  const [data, setData] = useState<any>(null);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [previewError, setPreviewError] = useState(false);
  const [conformanceClass, setConformanceClass] = useState("legalMinimum");
  const [submitting, setSubmitting] = useState(false);
  const conformanceClassRef = useRef(conformanceClass);
  const previewRequestId = useRef(0);

  // Conformity verdicts (LEGAL-NN-C, Mode B) — only obligations the engine
  // evaluated against this asset (carry an annotated `trace`). Dormant until the
  // binding API attaches conformity evaluations; the DIN unit fetch is skipped
  // until then.
  const evaluatedObligations: any[] = Array.isArray(data?.obligations)
    ? data.obligations.filter((o: any) => o?.conformity && (o?.result || o?.trace))
    : [];
  // Status roll-up across the asset's conformity obligations.
  const conformitySummary = evaluatedObligations.reduce((acc: Record<string, number>, o: any) => {
    const status = (o.result ?? o.trace)?.status;
    if (status) acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  // true / false / unknown + n/a — Badge-Variante + Beschriftung je ConformityStatus.
  const statusTone = (status: string): { variant: "success" | "destructive" | "warning" | "secondary"; label: string } =>
    ({
      Compliant: { variant: "success" as const, label: t("conformity.short.true", "erfüllt") },
      Violated: { variant: "destructive" as const, label: t("conformity.short.false", "nicht erfüllt") },
      NotDeterminable: { variant: "warning" as const, label: t("conformity.short.unknown", "unbestimmt") },
      NotApplicable: { variant: "secondary" as const, label: t("conformity.short.na", "nicht zutreffend") },
    } as Record<string, { variant: "success" | "destructive" | "warning" | "secondary"; label: string }>)[status] || {
      variant: "secondary" as const,
      label: status,
    };

  // Reading-atom status (VDMA measurement subset): T/F/U with the stale/missing reason.
  // Resolver-agnostic — a manual capture and a sensor look identical here.
  const atomTone = (value: string, readingState?: string): { variant: "success" | "destructive" | "warning"; label: string } => {
    if (value === "T") return { variant: "success", label: t("conformity.short.true", "erfüllt") };
    if (value === "F") return { variant: "destructive", label: t("conformity.short.false", "nicht erfüllt") };
    const reason =
      readingState === "stale"
        ? t("maintenance.readingStale", "Messwert veraltet")
        : t("maintenance.readingMissing", "kein Messwert");
    return { variant: "warning", label: reason };
  };

  // Interval → human label. Months are open (VDMA leaves frequency to the legal "Dass").
  const intervalLabel = (iv: any): string => {
    if (!iv) return "";
    if (typeof iv === "string") return iv;
    if (iv.modus === "beiBedarf") return t("maintenance.onDemand", "bei Bedarf");
    if (iv.months) {
      const base = t("maintenance.everyNMonths", { count: iv.months, defaultValue: "alle {{count}} Monate" });
      // Provenance: norm-derived is the default; an operator default is honestly marked.
      return iv.intervalSource === "operatorDefault"
        ? `${base} ${t("maintenance.operatorDefaultTag", "(Betreiber-Vorgabe)")}`
        : base;
    }
    // periodisch without a resolved frequency is NOT an interval — never "periodisch"/"einmalig".
    return t("maintenance.intervalPending", "Intervall folgt");
  };

  const load = async () => {
    if (!assetId || !authentication?.customer) {
      return;
    }

    try {
      setData(await legalAPI.getAssetObligations(assetId));
    } catch (error) {
      pushError(error);
    }
  };

  // Capturing a reading is a field/task action (never a rule edit); it flips the atom U→T/F.
  const captureReading = async (item: any) => {
    const attributeName = item?.reading?.attributeName;
    if (!attributeName || !assetId) return;
    const raw = window.prompt(
      t("maintenance.enterValue", {
        attr: attributeName,
        unit: item?.reading?.unit || "",
        defaultValue: "Messwert für {{attr}} {{unit}}",
      }),
    );
    if (raw == null || raw.trim() === "") return;
    const num = Number(raw.replace(",", "."));
    try {
      await legalAPI.captureReading(assetId, {
        attributeName,
        value: Number.isNaN(num) ? raw.trim() : num,
        unit: item?.reading?.unit,
      });
      await load();
    } catch (error) {
      pushError(error);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, authentication?.customer]);

  // The scope is fixed (this asset's site × regulated object), so the
  // mandatory effect preview runs automatically when the dialog opens —
  // "Aktivieren" unlocks as soon as the consequence is visible.
  useEffect(() => {
    if (activateDialogOpen && !preview && !previewError && !submitting) {
      runPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activateDialogOpen, preview, previewError, submitting, conformanceClass]);

  const subscriptionScope = () => ({
    anchor: { type: "Site" as const, siteId: String(data.siteId) },
    regulatedObjects: [data.regulatedObjectCode] as string[],
  });

  const runPreview = async () => {
    const requestId = ++previewRequestId.current;
    const requestClass = conformanceClass;
    setSubmitting(true);
    setPreviewError(false);

    try {
      const previewResult = await legalAPI.previewBinding(subscriptionScope(), null, requestClass);
      if (previewRequestId.current === requestId && conformanceClassRef.current === requestClass) {
        setPreview(previewResult);
      }
    } catch (error) {
      if (previewRequestId.current === requestId && conformanceClassRef.current === requestClass) {
        pushError(error);
        setPreviewError(true);
      }
    } finally {
      if (previewRequestId.current === requestId) {
        setSubmitting(false);
      }
    }
  };

  const activate = async () => {
    setSubmitting(true);

    try {
      await legalAPI.createBinding(subscriptionScope(), null, conformanceClass);
      setActivateDialogOpen(false);
      setPreview(null);
      setPreviewError(false);
      await load();
    } catch (error) {
      pushError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const scopeSummary = (binding: any) => {
    const anchor =
      binding.scope?.anchor?.type === "Site"
        ? t("operatorObligationsPanel.site")
        : t("operatorObligationsPanel.portfolio");
    const objects =
      binding.scope?.regulatedObjects === "ALL"
        ? t("operatorObligationsPanel.allRegulatedObjects")
        : (binding.scope?.regulatedObjects || [])
            .map((code: string) => regulatedObjectName(t, code))
            .join(", ");

    return `${anchor} × ${objects}`;
  };

  const columns = useMemo<ColumnDef<any, any>[]>(() => [
    {
      id: "obligation",
      header: ({ column }) => (
        <SortableHeader column={column}>{t("operatorResponsibilityPanel.obligation")}</SortableHeader>
      ),
      accessorFn: (obligation: any) => obligation.obligationKey,
      size: 170,
    },
    {
      id: "description",
      header: t("operatorResponsibilityPanel.description"),
      accessorFn: (obligation: any) => obligation.description,
      cell: ({ row }) => <WrappedText text={row.original.description} maxWidth={380} fontSize={12} />,
    },
    {
      id: "tier",
      header: ({ column }) => (
        <SortableHeader column={column}>{t("operatorResponsibilityPanel.tier")}</SortableHeader>
      ),
      accessorFn: (obligation: any) => obligation.tier ?? "—",
      size: 70,
    },
    {
      id: "type",
      header: ({ column }) => (
        <SortableHeader column={column}>{t("operatorResponsibilityPanel.type")}</SortableHeader>
      ),
      accessorFn: (obligation: any) =>
        String(t(`operatorResponsibilityPanel.obligationType.${obligation.type}`, { defaultValue: obligation.type })),
      size: 150,
    },
    {
      id: "provenance",
      header: t("operatorResponsibilityPanel.provenance.label"),
      enableSorting: false,
      cell: ({ row }) => (
        <ProvenanceBadge provenanceType={row.original.provenanceType} generatedBy={row.original.generatedBy} />
      ),
      size: 170,
    },
    {
      id: "status",
      header: t("operatorResponsibilityPanel.status"),
      enableSorting: false,
      size: 150,
      cell: ({ row }) => <ReviewStatusBadge status={row.original.reviewStatus} />,
    },
  ], [t]);

  if (!data) {
    return <div className="text-sm">{t("global.loading")}</div>;
  }

  if (!data.regulatedObjectCode) {
    return (
      <div className="text-sm text-muted-foreground">
        {t("operatorObligationsPanel.assetNotMapped")}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 max-w-[1400px] flex-col gap-4">
      <div className="text-sm">
        {t("operatorResponsibilityPanel.regulatedObject")}:{" "}
        <b>{regulatedObjectName(t, data.regulatedObjectCode)}</b>
        {data.tier != null ? ` (${t("operatorResponsibilityPanel.tierLegend.tierLabelShort", { tier: data.tier })})` : ""}
      </div>
      {/* Subscription status — additive bindings, status always explicit */}
      {data.inSubscription ? (
        <Alert variant="success">
          <AlertDescription>
            <b>{t("operatorObligationsPanel.assetSubscribed")}</b>{" "}
            {data.matchingBindings
              .map((binding: any) => scopeSummary(binding))
              .join(" · ")}
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="warning">
          <AlertDescription className="flex flex-wrap items-center gap-4">
            <b>{t("operatorObligationsPanel.assetNotSubscribed")}</b>
            {canWrite && data.siteId ? (
              <Button
                type="button"
                onClick={() => {
                  conformanceClassRef.current = "legalMinimum";
                  setPreview(null);
                  setPreviewError(false);
                  setConformanceClass("legalMinimum");
                  setActivateDialogOpen(true);
                }}
              >
                {t("operatorObligationsPanel.createBinding")}
              </Button>
            ) : null}
            {!data.siteId ? <span>{t("operatorObligationsPanel.assetMissingSite")}</span> : null}
          </AlertDescription>
        </Alert>
      )}
      <PflichtklasseLegend />
      <div className="overflow-x-auto">
        <DataTable
          columns={columns}
          data={data.obligations}
          pageSize={5}
          emptyMessage={t("settingsPanel.dataTable.noRecordsText")}
        />
      </div>

      {evaluatedObligations.length > 0 && (
        <div className="flex min-w-0 flex-col gap-2">
          <h3 className="text-lg font-bold leading-tight">{t("conformity.title", "Konformität")}</h3>
          {/* Roll-up: erfüllt / nicht erfüllt / unbestimmt. */}
          <div className="flex flex-wrap gap-4">
            {["Compliant", "Violated", "NotDeterminable", "NotApplicable"]
              .filter((status) => conformitySummary[status])
              .map((status) => {
                const tone = statusTone(status);
                return (
                  <Badge key={status} variant={tone.variant} className="gap-1.5">
                    <b>{conformitySummary[status]}</b> {tone.label}
                  </Badge>
                );
              })}
          </div>
          {/* Plain list: obligation · status · (if unknown) what is missing. */}
          {evaluatedObligations.map((o: any) => {
            const res = o.result ?? o.trace;
            const status: string = res?.status || "NotDeterminable";
            const tone = statusTone(status);
            const whereLabel: Record<string, string> = { asset: "Asset", part: "Teil", building: "Gebäude" };
            const missing: string[] =
              status === "NotDeterminable"
                ? (res?.missing || []).map((m: any) =>
                    m.typeCode && !m.attr
                      ? `${m.typeCode} (${t("conformity.partMissing", "Teil fehlt")})`
                      : `${m.attr}${m.where ? ` (${whereLabel[m.where] || m.where})` : ""}`,
                  )
                : [];
            return (
              <div
                key={o.obligationKey}
                className="flex min-w-0 flex-wrap items-center gap-4 border-b pb-1.5"
              >
                <Badge variant={tone.variant} className="min-w-[104px] shrink-0 justify-center">
                  {tone.label}
                </Badge>
                <span className="min-w-0 flex-1 break-words">{o.description || o.obligationKey}</span>
                {missing.length > 0 && (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {t("conformity.missingLabel", "fehlt")}: {missing.join(", ")}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Wartungs-Checkliste (VDMA 24186): Tätigkeit · Intervall · Messziel.
          Procedural items (activity/quittung) carry no atom; measurement items show
          the reading atom's T/F/U status + stale/missing reason, resolver-agnostic. */}
      {Array.isArray(data.maintenanceChecklist) && data.maintenanceChecklist.length > 0 && (
        <div className="flex min-w-0 flex-col gap-2">
          <h3 className="text-lg font-bold leading-tight">{t("maintenance.title", "Wartungs-Checkliste (VDMA 24186)")}</h3>
          <div className="max-h-[420px] overflow-auto rounded-md border">
            <Table>
              <TableHeader className="sticky top-0 bg-muted">
                <TableRow>
                  <TableHead>{t("maintenance.task", "Tätigkeit")}</TableHead>
                  <TableHead className="w-[130px]">{t("maintenance.interval", "Intervall")}</TableHead>
                  <TableHead className="w-[220px]">{t("maintenance.target", "Messziel")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.maintenanceChecklist.map((item: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell className="min-w-0 break-words">
                      {item.task}
                      <span className="ml-1.5 text-xs text-muted-foreground">· {item.normbezug}</span>
                    </TableCell>
                    <TableCell>{intervalLabel(item.intervall)}</TableCell>
                    <TableCell>
                      {item.kind === "measurement" && item.atom ? (
                        <span className="flex flex-wrap items-center gap-2">
                          <Badge variant={atomTone(item.atom.value, item.atom.readingState).variant}>
                            {atomTone(item.atom.value, item.atom.readingState).label}
                          </Badge>
                          {(item.sollMissing || item.atom?.sollWert?.cmp === "exists") && (
                            <span className="text-xs text-muted-foreground" title={t("maintenance.sollMissingHint", "Messwert wird erfasst, aber es ist noch kein Sollwert hinterlegt") as string}>
                              {t("maintenance.sollMissing", "Soll fehlt")}
                            </span>
                          )}
                          {canWrite && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => captureReading(item)}
                            >
                              {t("maintenance.captureReading", "Wert erfassen")}
                            </Button>
                          )}
                        </span>
                      ) : item.kind === "quittung" ? (
                        <span className="flex items-center gap-2 text-muted-foreground">
                          <Checkbox disabled title={t("maintenance.quittungHint", "Quittung – im Aufgaben-/Feldkontext bestätigen") as string} />
                          {t("maintenance.quittung", "Quittung")}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Activation with the mandatory effect preview, scoped to the asset's
          site × regulated object — same mechanism as the settings panel. */}
      <Dialog
        open={activateDialogOpen}
        onOpenChange={(next) => {
          if (!next) setActivateDialogOpen(false);
        }}
      >
        <DialogContent className="max-w-none sm:w-[560px] w-[calc(100vw-2rem)]">
          <DialogHeader>
            <DialogTitle>{t("operatorObligationsPanel.createBinding")}</DialogTitle>
            <DialogDescription>{t("operatorObligationsPanel.bindingEditorSubtitle")}</DialogDescription>
          </DialogHeader>
          <div className="text-sm">
            {t("operatorObligationsPanel.anchor")}: <b>{t("operatorObligationsPanel.site")}</b> ×{" "}
            <b>{regulatedObjectName(t, data.regulatedObjectCode)}</b>
          </div>
          <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor={conformanceClassId}>{t("operatorObligationsPanel.conformanceClass")}</Label>
            <Select
              value={conformanceClass}
              disabled={submitting}
              onValueChange={(value: string) => {
                const nextClass = String(value);
                conformanceClassRef.current = nextClass;
                setConformanceClass(nextClass);
                setPreview(null);
                setPreviewError(false);
              }}
            >
              <SelectTrigger id={conformanceClassId} className="w-[320px] max-w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-w-[calc(100vw-2rem)]">
                <SelectItem value="legalMinimum" className="whitespace-normal">
                  {t("operatorObligationsPanel.conformance.legalMinimum")}
                </SelectItem>
                <SelectItem value="recognizedRulesOfTechnology" className="whitespace-normal">
                  {t("operatorObligationsPanel.conformance.recognizedRulesOfTechnology")}
                </SelectItem>
                <SelectItem value="bestPractice" className="whitespace-normal">
                  {t("operatorObligationsPanel.conformance.bestPractice")}
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t("operatorObligationsPanel.conformanceNote")}
            </p>
          </div>
          {preview ? (
            <Alert variant="info">
              <AlertTitle>{t("operatorObligationsPanel.previewTitle")}</AlertTitle>
              <AlertDescription>
                {t("operatorObligationsPanel.previewSummary", {
                  assets: preview.assetsMatched,
                  sites: preview.sitesCovered,
                  obligations: preview.obligationsMatched,
                  oneTime: preview.oneTimeChecklistsNow,
                  plans: preview.periodicPlans,
                  faults: preview.faultTriggersArmed,
                })}
              </AlertDescription>
            </Alert>
          ) : previewError ? (
            <Alert variant="destructive">
              <AlertTitle>{t("operatorObligationsPanel.previewLoadFailedTitle")}</AlertTitle>
              <AlertDescription className="flex flex-col items-start gap-2">
                <span>{t("operatorObligationsPanel.previewLoadFailed")}</span>
                <Button type="button" variant="outline" disabled={submitting} onClick={runPreview}>
                  {t("global.refresh")}
                </Button>
              </AlertDescription>
            </Alert>
          ) : (
            <div className="text-sm text-muted-foreground">
              {t("global.loading")}…
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setActivateDialogOpen(false)}>
              {t("global.cancel")}
            </Button>
            {/* Commit blocked until the (auto-loaded) preview is visible */}
            <Button type="button" disabled={!preview || submitting} onClick={activate}>
              {t("operatorObligationsPanel.activate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
